import React, { forwardRef, useEffect, useImperativeHandle, useCallback, useMemo } from 'react';
import { uploadFile } from '@/services/common/api';
import { Editor } from '@tinymce/tinymce-react';
import { Image } from 'antd';

export type RichProps = {
  data?: string;
  onChange?: (value: string) => void;
  onEdit?: () => void;
  height?: number | string;
  readOnly: boolean;
  extendControls?: React.ReactNode;
  placeholder?: string;
  stickyToolbar?: boolean;
  noBorder?: boolean; // 新增：是否隐藏边框
};

export interface RichTextHandles {
  description: string | undefined;
}

const RichTextEditor = forwardRef<RichTextHandles, RichProps>((props, ref) => {
  const { data, onChange, onEdit, height, readOnly, extendControls, placeholder, stickyToolbar, noBorder } =
    props;

  const [content, setContent] = React.useState<string | undefined>(data);
  const [visible, setVisible] = React.useState(false);
  const [previewImage, setPreviewImage] = React.useState('');

  // Update content when data prop changes
  useEffect(() => {
    setContent(data ?? '');
  }, [data, readOnly]);

  // Expose content to parent via ref
  useImperativeHandle(ref, () => ({
    description: content,
  }));

  // Handle image upload
  const handleUpload = useCallback(
    async (blobInfo: any, success: (url: string) => void, failure: (err: string) => void) => {
      try {
        const fd = new FormData();
        fd.append('file', blobInfo.blob());
        fd.append('permissionType', 'public-read');

        const { resultObject } = await uploadFile(fd);
        if (resultObject?.fileUrl) {
          success(resultObject.fileUrl);
        } else {
          failure('Upload failed');
        }
      } catch (error) {
        console.error('Upload error:', error);
        failure('Upload failed');
      }
    },
    [],
  );

  // Handle editor content change
  const handleEditorChange = useCallback(
    (value: string) => {
      setContent(value);
      onChange?.(value);
    },
    [onChange],
  );

  // Handle image click for preview
  const handleImageClick = (event) => {
    if (event.target.nodeName === 'IMG') {
      const imageUrl = event.target.src; // 获取图片的src链接
      setPreviewImage(imageUrl);
      setVisible(true);
    }
  };

  const processContent = (content) => {
    // 创建一个临时的div容器来处理HTML内容
    const div = document.createElement('div');
    div.innerHTML = content;

    // 查找所有外部链接（href属性以 "http" 开头的链接）
    const links = div.querySelectorAll('a[href^="http"]');
    links.forEach((link) => {
      // 如果没有target属性，就添加target="_blank"
      if (!link.hasAttribute('target')) {
        link.setAttribute('target', '_blank');
      }
      if (!link.hasAttribute('rel')) {
        link.setAttribute('rel', 'noopener noreferrer');
      }
    });

    // 查找所有图片
    const images = div.querySelectorAll('img');
    images.forEach((img) => {
      // 如果没有width属性，或width超出100%，则设置width为100%
      if (!img.style.width && !img.hasAttribute('width') && !img.getAttribute('data-resized')) {
        img.style.width = '100%';
        img.style.height = 'auto';
        img.setAttribute('data-resized', 'true');
      }
    });
    // 返回处理后的HTML内容
    return div.innerHTML;
  };

  // Clean up TinyMCE autosave on unmount
  useEffect(() => {
    const cleanupAutosave = () => {
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('tinymce-autosave-')) {
          localStorage.removeItem(key);
        }
      });
    };

    return () => {
      cleanupAutosave();
    };
  }, []);

  // Common editor configuration
  const editorConfig = useMemo(
    () => ({
      height: height ?? undefined,
      min_height: 200,
      max_height: 1000,
      language: 'zh_CN',
      placeholder: placeholder ?? '请输入内容',
      plugins: [
        'print preview searchreplace autolink directionality visualblocks visualchars',
        'fullscreen image link media template code codesample table charmap hr pagebreak',
        'nonbreaking anchor insertdatetime advlist lists wordcount imagetools',
        'textpattern help emoticons autosave bdmap indent2em formatpainter axupimgs paste',
        ...(height ? [] : ['autoresize']),
      ].join(' '),
      toolbar: [
        'forecolor backcolor bold italic underline strikethrough link',
        'formatselect fontsizeselect styleselect fontselect',
        'alignleft aligncenter alignright alignjustify outdent indent',
        'bullist numlist blockquote subscript superscript removeformat',
        'table image charmap emoticons hr pagebreak insertdatetime',
        'print preview anchor code codesample fullscreen bdmap indent2em',
        'lineheight formatpainter axupimgs',
      ].join(' | '),
      fontsize_formats: '12px 14px 16px 18px 24px 36px 48px 56px 72px',
      font_formats: [
        '微软雅黑=Microsoft YaHei,Helvetica Neue,PingFang SC,sans-serif',
        '苹果苹方=PingFang SC,Microsoft YaHei,sans-serif',
        '宋体=simsun,serif',
        '仿宋体=FangSong,serif',
        '黑体=SimHei,sans-serif',
        'Arial=arial,helvetica,sans-serif',
        'Arial Black=arial black,avant garde',
        'Book Antiqua=book antiqua,palatino',
      ].join(';'),
      paste_data_images: true,
      paste_retain_style_properties: 'color font-size',
      images_upload_handler: handleUpload,
      default_link_target: '_blank',
      content_style: `body { font-family:Helvetica,Arial,sans-serif; font-size:14px ${noBorder ? '; padding: 0; margin: 0;' : ''} }`,
      toolbar_sticky: stickyToolbar,
      autosave_ask_before_unload: false,
      ...(noBorder && {
        statusbar: false,
        menubar: false,
      }),
      setup: (editor: any) => {
        editor.on('BeforeSetContent', (e: any) => {
          if (e?.content) {
            e.content = processContent(e.content);
          }
        });

        // 隐藏边框的样式处理
        if (noBorder) {
          editor.on('init', () => {
            const container = editor.getContainer();
            if (container) {
              container.style.border = 'none';
              container.style.boxShadow = 'none';
              const tinymceElement = container.querySelector('.tox-tinymce');
              if (tinymceElement) {
                tinymceElement.style.border = 'none';
                tinymceElement.style.boxShadow = 'none';
              }
            }
          });
        }
      },
    }),
    [height, placeholder, stickyToolbar, noBorder, handleUpload, processContent],
  );

  // Read-only editor configuration
  const readOnlyConfig = useMemo(
    () => ({
      ...editorConfig,
      menubar: false,
      toolbar: '',
      statusbar: false,
      setup: (editor: any) => {
        editor.on('init', () => {
          editor.getBody().setAttribute('contenteditable', 'false');

          // 只读模式下的边框隐藏处理
          if (noBorder) {
            const container = editor.getContainer();
            if (container) {
              container.style.border = 'none';
              container.style.boxShadow = 'none';
              const tinymceElement = container.querySelector('.tox-tinymce');
              if (tinymceElement) {
                tinymceElement.style.border = 'none';
                tinymceElement.style.boxShadow = 'none';
              }
            }
          }
        });
        editor.on('BeforeSetContent', (e: any) => {
          e.content = processContent(e.content);
        });
        editor.on('click', handleImageClick);
        editor.on('dblclick', onEdit);
      },
      content_style: `body { pointer-events: auto; cursor: default; ${noBorder ? 'padding: 0; margin: 0;' : ''} }`,
    }),
    [editorConfig, noBorder, handleImageClick, onEdit, processContent],
  );

  // 容器样式
  const containerStyle = noBorder ? {
    '& .tox': {
      border: 'none !important',
    },
    '& .tox-tinymce': {
      border: 'none !important',
      boxShadow: 'none !important',
    },
    '& .tox-statusbar': {
      display: 'none !important',
    },
  } : {};

  return (
    <div style={containerStyle}>
      {!readOnly && extendControls}
      {props.readOnly && (
        <Editor value={content} init={readOnlyConfig} tinymceScriptSrc="/tinymce/tinymce.min.js" />
      )}
      {!props.readOnly && (
        <Editor
          value={content}
          init={editorConfig}
          tinymceScriptSrc="/tinymce/tinymce.min.js"
          onEditorChange={handleEditorChange}
        />
      )}
      <Image.PreviewGroup
        preview={{
          visible,
          onVisibleChange: setVisible,
          current: 0,
        }}
      >
        <Image src={previewImage} style={{ display: 'none' }} />
      </Image.PreviewGroup>
    </div>
  );
});

export default RichTextEditor;